import ErrorStackParser from 'error-stack-parser';
import { omit, uniq } from 'lodash-es';
import { fromError } from 'zod-validation-error';

import { isFlowError } from '#flows.js';
import { knownSerializers } from '#serializers.js';
import { isFn } from '#types.js';
import { shorten, tryStringifyJson } from '#utils.js';
import { isZodError } from '#zod/types.js';

export type ErrorMetadata = Partial<Record<string, unknown>>;

export interface DetailedErrorArgs<D extends Record<string, unknown>> {
  cause?: Error | unknown;
  details?: (D & Record<string, unknown>) | undefined;
}

export function errorMessage(
  error: unknown,
  { prefix = '', suffix = '', firstLineOnly = false, fallbackMessage = 'Unknown error', includeStack = false } = {},
): string {
  if (error instanceof Error) {
    if (isZodError(error)) {
      return fromError(error, { includePath: true }).toString();
    } else {
      let buf = prefix;
      buf += error.message || fallbackMessage;
      buf += suffix;
      if (error.cause) buf += ` (${errorMessage(error.cause)})`;
      if (includeStack) buf += '\n' + error.stack;
      if (firstLineOnly) buf = buf.split('\n')[0] ?? buf;
      return buf.trim();
    }
  } else if (typeof error === 'string') {
    return `${prefix}${error.trim()}${suffix}`;
  } else if (typeof error === 'object' && error !== null && 'message' in error) {
    return `${prefix}${String((error as { message: unknown }).message || fallbackMessage).trim()}${suffix}`;
  } else if (isFlowError(error)) {
    return `${prefix}${(error?.error || fallbackMessage).trim()}${suffix}`;
  } else if (typeof error === 'object' && error !== null) {
    return `${prefix}${fallbackMessage}${shorten(String(error))}${suffix}`;
  } else {
    return `${prefix}${String(error || fallbackMessage).trim()}${suffix}`;
  }
}

export class DetailedError<Details extends Record<string, unknown> = Record<string, any>> extends Error {
  static catch<D extends Record<string, unknown>, R>(
    message: string,
    details: DetailedErrorArgs<D>['details'],
    bodyFn: () => R,
  ): R {
    try {
      const ret = bodyFn();
      if (ret instanceof Promise) {
        return ret.catch((cause) => {
          throw new this(message, { details, cause });
        }) as R;
      }
      return ret;
    } catch (cause) {
      throw new this(message, { details, cause });
    }
  }

  static onError(handler: (error: DetailedError) => void): void {
    DetailedError.onErrorHandlers.push((error) => {
      if (DetailedError.#handledErrors.has(error)) return;
      DetailedError.#handledErrors.add(error);
      handler(error);
    });
  }

  protected static readonly onErrorHandlers: Array<(error: DetailedError) => void> = [];

  protected static runErrorHandlers(error: DetailedError): void {
    if (this.#handledErrors.has(error)) return;
    else this.#handledErrors.add(error);
    for (const handler of DetailedError.onErrorHandlers) {
      handler(error);
    }
  }

  static #handledErrors = new WeakSet<DetailedError>();
  static readonly omitKeys = uniq([
    'cause',
    'stack',
    'message',
    'name',
    'triggeredAt',
    ...Object.keys(DetailedError.prototype),
  ]);
  #details?: Record<string, any>;

  constructor(
    message: string,
    { cause, details = {} as Details }: DetailedErrorArgs<Details> = {} as DetailedErrorArgs<Details>,
  ) {
    super(message, { cause });
    this.#details = omit(details, DetailedError.omitKeys);
    this.cause = cause;
    this.name = this.constructor.name;
    this.triggeredAt = new Date(); // Set timestamp BEFORE running handlers that might log the error
    Object.assign(this, this.#details);
    DetailedError.runErrorHandlers(this);
  }

  getDetail<K extends keyof Details & string>(name: K): Details[K] | undefined {
    return this.#details?.[name] as Details[K] | undefined;
  }
  readonly triggeredAt: Date;

  toJSON() {
    const cause = typeof this.cause === 'object' && this.cause !== null ? this.cause : undefined;
    let jsonCause: Record<string, unknown> | undefined;
    if (cause) {
      if (cause instanceof Error) {
        jsonCause = knownSerializers.errorWithCause(cause);
      } else if ('toJSON' in cause && isFn<Record<string, unknown>>(0, cause.toJSON)) {
        jsonCause = cause.toJSON();
      } else {
        const buf = tryStringifyJson(cause);
        jsonCause = buf ? (JSON.parse(buf) as Record<string, unknown>) : { message: String(cause) };
      }
    }
    return {
      ...this.#details,
      message: this.message,
      name: this.name,
      stack: this.stack,
      cause: jsonCause,
      triggeredAt: this.triggeredAt?.toISOString() ?? new Date().toISOString(),
    } as const;
  }
}

export class FatalError extends DetailedError {
  constructor(message: string, metadata?: Record<string, unknown>) {
    super(message, metadata);
    this.name = 'FatalError';
  }

  fatal = true;
}

export class Bug extends FatalError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, { details });
    this.name = 'Bug';
  }
}

export function assert(condition: unknown, message?: string, metadata?: Record<string, unknown>): asserts condition {
  if (!condition) {
    throw new Bug(message || 'Assertion failed', metadata);
  }
}

export function sourcePathBasename(path: string): string {
  let name = path.split('/').at(-1);
  name ??= path.slice(-30);
  name ??= path.replace(/^.*\/(src|dist)/, '$1');
  return name ?? path;
}

export function getCaller(offset = 0, stackProvider = new Error()): string | undefined {
  const stack = ErrorStackParser.parse(stackProvider)
    .slice(2)
    .map((frame) => `${frame.functionName}@${sourcePathBasename(frame.fileName ?? '')}`);
  return stack[offset] || undefined;
}
